import { useState } from 'react'
import { X, Copy, Check, MessageCircle, Send } from 'lucide-react'

interface ReferralShareModalProps {
  referralCode: string
  referralUrl: string
  onClose: () => void
}

function ReferralShareModal({ referralCode, referralUrl, onClose }: ReferralShareModalProps) {
  const [copied, setCopied] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)

  const shareMessage = `Hey! I use Scouta to discover and connect with football talent. Sign up with my referral code "${referralCode}" and upgrade to Pro — I'll earn a reward! 🏆\n\n${referralUrl}`

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(referralUrl)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank')
  }

  const handleTwitter = () => {
    const tweet = `I use Scouta for football scouting! Sign up with my code "${referralCode}" and go Pro — I'll earn a reward! 🏆\n\n${referralUrl}`
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`, '_blank')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-3">
      <div className="w-full max-w-md rounded-[28px] border border-white/10 bg-[#07141a] p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-white">Share Your Referral</h3>
          <button onClick={onClose} className="rounded-full border border-white/10 bg-white/5 p-2 text-gray-300 hover:bg-white/10 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <p className="mt-3 text-sm text-slate-400">Share your referral code or link with friends. When they upgrade to Pro, you earn 20 tokens (₦2,000)!</p>

        <div className="mt-5 space-y-3">
          <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2">Referral Code</p>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-black text-[#7cff01] tracking-widest">{referralCode}</span>
              <button onClick={handleCopyCode} className="text-slate-400 hover:text-white transition" title="Copy code">
                {copied ? <Check size={20} className="text-green-400" /> : <Copy size={20} />}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-2">Referral Link</p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-slate-300 truncate flex-1">{referralUrl}</p>
              <button onClick={handleCopyUrl} className="text-slate-400 hover:text-white transition flex-shrink-0" title="Copy link">
                {copiedUrl ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            onClick={handleWhatsApp}
            className="flex items-center justify-center gap-2 rounded-2xl bg-green-600 px-4 py-3 text-sm font-black text-white transition hover:bg-green-500"
          >
            <MessageCircle size={16} />
            WhatsApp
          </button>
          <button
            onClick={handleTwitter}
            className="flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-black text-white transition hover:bg-sky-500"
          >
            <Send size={16} />
            Twitter / X
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReferralShareModal
